<template>
  <div class="confirm-email-container">
    <GameAlert
      :show="!!alertConfig.message"
      :type="alertConfig.type"
      :message="alertConfig.message"
      @close="closeAlert"
    />
    
    <div class="confirm-email-card">
      <!-- Show success message when coming from /confirm -->
      <template v-if="confirmado">
        <h1>{{ $t('confirmEmail.title') }}</h1>
        <div class="success">
          <i class="mail-icon">✅</i>
          <p>{{ $t('confirmEmail.success') }}</p>
          <router-link to="/" class="btn primary">
            {{ $t('confirmEmail.goToLogin') }}
          </router-link>
        </div>
      </template>

      <!-- Show resend options when coming from /pending -->
      <template v-else>
        <h1>{{ $t('confirmEmail.pendingTitle') }}</h1>
        <div class="info-message">
          <i class="mail-icon">📧</i>
          <p>{{ $t('confirmEmail.pleaseConfirm') }}</p>
          <p class="sub-text">{{ $t('confirmEmail.checkInbox') }}</p>
          <button 
            @click="resendConfirmation" 
            :disabled="resendCooldown > 0"
            class="btn secondary"
          >
            {{ resendCooldown > 0 
              ? $t('confirmEmail.waitSeconds', { seconds: resendCooldown })
              : $t('confirmEmail.resendEmail') 
            }}
          </button>
          <router-link to="/" class="btn primary">
            {{ $t('confirmEmail.backToLogin') }}
          </router-link>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import * as v2 from '@/core/services/api/v2';
import GameAlert from '../shared/common/Alert.vue';

export default {
  name: 'ConfirmEmail',
  components: {
    GameAlert
  },
  data() {
    return {
      confirmado: false,
      resendCooldown: 0,
      resendTimer: null,
      alertConfig: {
        message: '',
        type: 'info'
      }
    }
  },
  computed: {
    /** O codigo chega pelo link do e-mail: /confirm-email?code=... */
    codigo() {
      return this.$route.query.code;
    }
  },
  /**
   * Com codigo na URL, confirma sozinho: o jogador clicou no link do e-mail e
   * nao deveria precisar apertar mais nada.
   */
  async created() {
    if (!this.codigo) return;
    try {
      await v2.confirmarEmail(this.codigo);
      this.confirmado = true;
    } catch (err) {
      const mensagem =
        err.response?.data?.message || err.response?.data?.error?.message || err.message;
      this.showAlert(mensagem || this.$t('confirmEmail.resendError'), 'error');
    }
  },

  methods: {
    /**
     * Reenvia o link. E autenticada: o alvo e sempre a propria conta — o V2
     * nao aceita "confirme o e-mail do vizinho", e por isso nao ha campo de
     * e-mail aqui (o `auth.pendingEmail` que a versao antiga lia nunca
     * existiu no state).
     */
    async resendConfirmation() {
      try {
        await v2.reenviarConfirmacao();
        this.resendCooldown = 30;
        this.startResendTimer();
        this.showAlert(this.$t('confirmEmail.resendSuccess'), 'success');
      } catch (err) {
        const mensagem =
          err.response?.data?.message || err.response?.data?.error?.message || err.message;
        console.error('Error resending confirmation:', mensagem);
        this.showAlert(this.$t('confirmEmail.resendError'), 'error');
      }
    },
    startResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      
      this.resendTimer = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        } else {
          clearInterval(this.resendTimer);
        }
      }, 1000);
    },
    showAlert(message, type = 'info') {
      this.alertConfig = { message, type };
      setTimeout(() => this.closeAlert(), 3000);
    },
    closeAlert() {
      this.alertConfig.message = '';
    }
  },
  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }
}
</script>

<style scoped>
.confirm-email-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
  position: relative;
}

.confirm-email-card {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

h1 {
  color: #333;
  margin-bottom: 1.5rem;
}

.loading, .success, .error {
  margin: 1rem 0;
}

.success {
  color: #4caf50;
}

.error {
  color: #f44336;
}

.btn {
  display: inline-block;
  margin: 0.5rem;
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: var(--f-2);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  text-decoration: none;
}
@media (hover: hover) {
  .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}

.btn.primary {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
}

.btn.secondary {
  background: linear-gradient(135deg, #2196f3, #1976d2);
  color: white;
}

.btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.info-message {
  padding: 2rem 0;
}

.mail-icon {
  font-size: var(--f-7);
  margin-bottom: 1rem;
  display: block;
}

.sub-text {
  font-size: var(--f-2);
  color: #666;
  margin-top: 0.5rem;
}

.resend-btn {
  margin: 1rem 0;
  opacity: 1;
  transition: opacity 0.3s;
}

.resend-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
